import React, { useEffect, useRef, useState } from "react";
import { ToolTip, PolyIcon } from "elements";
import { useTranslation } from "utilities/i18n";

// @help - https://github.com/typescript-cheatsheets/react-typescript-cheatsheet#useful-react-prop-type-examples
export interface Field {
  key: string;
  label: string;
  content: React.ReactNode;
  indicator?: boolean;
  "data-testid"?: string;
}

export interface CardProps {
  children: any;
  className?: string;
  fluid?: boolean;
  "data-testid"?: string;
}

export interface ExpandableCardProps {
  collapsedContent: React.ReactNode;
  image?: React.ReactNode;
  dropdown?: boolean;
  expanded?: boolean;
  scrollIntoView?: boolean;
  tooltip?: string;
  disabled?: boolean;
  titleCard?: boolean;
  styling?: string[];
}

/**
 * Render a "Card" with arbitrary content.
 *
 * @param children
 * @param className
 * @param fluid
 */
export const Card: React.FC<CardProps> = ({
  children,
  className,
  fluid,
  ...rest
}) => {
  const classes = ["card", className, fluid && "fluid"]
    .filter(c => !!c)
    .join(" ");
  return (
    <div className={classes} data-testid={rest["data-testid"]}>
      {children}
    </div>
  );
};

/**
 * Creates a card with an "expandable" section.
 * Content in the expandable section is not visible by default, becomes
 * visible when the down-arrow is clicked.
 *
 * @example
 * const DeviceCard = ({device: Device}) =>
 * <ExpandableCard>{makeColumnContentForCard(transformDeviceToCardProps(device))}</ExpandableCard>
 *
 * @param expandedContent
 * @param collapsedContent
 * @param image
 */
export const ExpandableCard: React.FC<ExpandableCardProps> = ({
  collapsedContent,
  image,
  dropdown,
  expanded,
  scrollIntoView,
  tooltip,
  children,
  disabled,
  titleCard = true,
  styling = []
}) => {
  const { t } = useTranslation();
  const self = useRef<HTMLDivElement>(null);
  const [isExpanded, setExpanded] = useState(expanded);
  const [isHover, setHover] = useState(false);

  useEffect(() => {
    if (dropdown && isExpanded && scrollIntoView) {
      self.current?.scrollIntoView({
        behavior: "smooth"
      });
    }
  }, [isExpanded, dropdown, scrollIntoView]);

  function toggleExpanded() {
    setExpanded(!isExpanded);
  }

  function toggleHover(type: string) {
    setHover("mouseenter" === type);
  }

  let icon = "";

  // in case of dropdown-like behavior always show the icon, not just on hover
  if (dropdown || isHover) {
    icon = isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down";
  }

  const title = isExpanded ? t("CLOSE") : t("OPEN");

  const hasImage = !!image;

  const containerClass = titleCard && (hasImage || dropdown) ? "grid" : "";
  const responsiveClass = hasImage ? (dropdown ? "col-xl-9" : "col-xl-10") : "";
  const expandControlClass = dropdown
    ? "col-1 expand-control-right"
    : "text-center col-12 expand-control";
  const iconClass = `controls ${dropdown ? "controls-right" : ""}`;
  if (disabled === true) {
    return (
      <div
        className={`card table-card expandable-card ${containerClass}`}
        data-testid="expandable-card"
      >
        <div className={`col-12 ${responsiveClass}`}>{collapsedContent}</div>
      </div>
    );
  }

  const classNames = styling.filter(c => !!c).join(" ");

  return (
    <>
      <div
        className={`card table-card expandable-card ${containerClass} ${classNames}`}
        data-testid="expandable-card"
        onMouseEnter={e => toggleHover(e.type)}
        onMouseLeave={e => toggleHover(e.type)}
        ref={self}
      >
        <div className={`col-12 ${responsiveClass}`}>
          {collapsedContent}
          {!dropdown && isExpanded && children}
        </div>
        {hasImage && (
          <div className="col-2 d-none d-block-xl center-container">
            <div className="center-item">{image}</div>
          </div>
        )}
        <ToolTip
          className={expandControlClass}
          disablePopup={!tooltip || isExpanded}
          iconReplacement={
            <div data-testid="arrow" onClick={toggleExpanded}>
              <PolyIcon className={iconClass} icon={icon} title={title} />
            </div>
          }
          iconTitle={title}
          position="bottom"
          size="sm"
        >
          <span>{tooltip}</span>
        </ToolTip>
      </div>
      {dropdown && isExpanded && children}
    </>
  );
};

export const CardField: React.FC<{
  width: number;
  className: string;
  "data-testid"?: string;
} & Pick<Field, "label">> = props => {
  const { width, className, label, children } = props;
  return (
    <div
      className={`col-${width} ${className}`}
      key={label}
      data-testid={props["data-testid"]}
    >
      <label>{label}</label>
      <span aria-label={label}>{children}</span>
    </div>
  );
};

/**
 * Receives an array of fields, and converts them into the "column" markup for rendering
 * in a 12-column layout.
 *
 * @param {Array<Field>} fields
 * @param {number} columns
 * @param {string} className
 * @param {boolean} hasIndicator
 *
 */
export const makeColumnContentForCard = (
  fields: Array<Field>,
  columns: 1 | 2 | 3 | 4 | 6 | 12,
  className: string = "",
  hasIndicator: boolean = false
) => {
  const width = 12 / columns;
  return (
    <>
      {hasIndicator && fields[0].content}
      <CardFields className={hasIndicator ? className + " inline" : className}>
        {fields.map(({ label, key, content, indicator, ...rest }) => {
          return !indicator ? (
            <CardField
              key={key}
              label={label}
              className={key}
              width={width}
              data-testid={rest["data-testid"]}
            >
              {content}
            </CardField>
          ) : null;
        })}
      </CardFields>
    </>
  );
};

/**
 * Receives an array of fields, and converts them into the "column" markup for rendering
 * in a 12-column layout.
 *
 * @param {Array<Field>} fields
 * @param {number} columns
 * @param {string} className
 * @param {boolean} hasIndicator
 *
 */
export const makeColumnContentForCardWithTitle = (
  fields: Array<Field>,
  columns: 1 | 2 | 3 | 4 | 6 | 12,
  className: string = "",
  hasIndicator: boolean = false,
  title: string = ""
) => {
  const width = 12 / columns;
  return (
    <>
      <h6 className="title mt-1 ml-1">{title}</h6>
      {hasIndicator && fields[0].content}
      <CardFields className={className}>
        {fields.map(({ label, key, content, indicator }) => {
          return !indicator ? (
            <CardField key={key} label={label} className={key} width={width}>
              {content}
            </CardField>
          ) : null;
        })}
      </CardFields>
    </>
  );
};

/**
 * Receives an array of fields, and converts them into the "column" markup for rendering
 * in a 10-column layout. This method differs from 'makeColumnContentForCard' in that it is meant
 * for a 10-column layout where as 'makeColumnContentForCard' is meant for a 12-column layout.
 *
 * @param {Array<Field>} fields
 * @param {number} columns
 * @param {string} className
 * @param {boolean} hasIndicator
 *
 */
export const makeColumnContentForCardBase10 = (
  fields: Array<Field>,
  columns: 1 | 2 | 5 | 10,
  className: string = "",
  hasIndicator: boolean = false
) => {
  const width = 10 / columns;
  return (
    <>
      {hasIndicator && fields[0].content}
      <CardFields className={hasIndicator ? className + " inline" : className}>
        {fields.map(({ label, key, content, indicator }) => {
          return !indicator ? (
            <CardField key={key} label={label} className={key} width={width}>
              {content}
            </CardField>
          ) : null;
        })}
      </CardFields>
    </>
  );
};

export const CardFields: React.FC<{
  className: string;
}> = ({ className, children }) => {
  const classes = `grid ${className}`;
  return <div className={classes}>{children}</div>;
};
